import React from "react";
import {
  Chart as ChartJS,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  ChartOptions
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(BarElement, LinearScale, CategoryScale, Tooltip, Legend);

interface HistogramChartProps {
  evictionsByReasonsPerYear: Record<string, Record<string, number>>;
}

const colorPalette = [
  "#A7C7E7", "#BFD3C1", "#89AEB2", "#D3DCE3", "#A5BFD6",
  "#C5D8E1", "#8FA7C7", "#E2E8F0", "#9DBAD5", "#B7C8DB",
  "#98AFC7", "#C0D3E0"
];

const HistogramChart: React.FC<HistogramChartProps> = ({ evictionsByReasonsPerYear }) => {
  const windowSize = 9;
  const hasInitialized = React.useRef(false);

  const [allYears, setAllYears] = React.useState<string[]>([]);
  const [reasons, setReasons] = React.useState<string[]>([]);
  const [startIndex, setStartIndex] = React.useState(0);
  const [chartData, setChartData] = React.useState<any>(null);

  const hasData = evictionsByReasonsPerYear && Object.keys(evictionsByReasonsPerYear).length > 0;

  // Effect 1: compute allYears & reasons (and drop future years)
  React.useEffect(() => {
    if (!hasData) return;

    let years = Object.keys(evictionsByReasonsPerYear).sort();

    // ✅ Fail-safe: drop future numeric years
    const currentYear = new Date().getFullYear();
    years = years.filter((y) => isNaN(Number(y)) || Number(y) <= currentYear);

    // Collect unique reasons across all (remaining) years
    const reasonsSet = new Set<string>();
    years.forEach((year) => {
      const byReason = evictionsByReasonsPerYear[year] || {};
      Object.keys(byReason).forEach((r) => reasonsSet.add(r));
    });

    setAllYears(years);
    setReasons(Array.from(reasonsSet).sort());
  }, [evictionsByReasonsPerYear, hasData]);

  // Effect 2: set default window once (show the most recent window)
  React.useEffect(() => {
    if (allYears.length > 0 && !hasInitialized.current) {
      const lastIndex = Math.max(0, allYears.length - windowSize);
      setStartIndex(lastIndex);
      hasInitialized.current = true;
    }
  }, [allYears, windowSize]);

  // Effect 3: build chart data for the visible window
  React.useEffect(() => {
    if (!hasData || allYears.length === 0) return;

    const visibleYears = allYears.slice(startIndex, startIndex + windowSize);

    const datasets = reasons.map((reason, index) => ({
      label: reason,
      data: visibleYears.map((year) => evictionsByReasonsPerYear[year]?.[reason] ?? 0),
      backgroundColor: colorPalette[index % colorPalette.length],
      borderColor: "#fff",
      borderWidth: 1,
      borderRadius: 4,
      stack: "stack-0",
    }));

    setChartData({
      labels: visibleYears,
      datasets,
    });
  }, [startIndex, allYears, reasons, evictionsByReasonsPerYear, hasData]);

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "Eviction Counts by Year and Reason",
        font: { size: 12, weight: "bold" },
        color: "#333",
        padding: { bottom: 10 },
        align: "center",
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          boxWidth: 12,
          padding: 10,
          font: { size: 10 },
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        backgroundColor: "#fff",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 8,
        bodyColor: "#333",
        titleColor: "#333",
        titleFont: { size: 10 },
        bodyFont: { size: 10 },
        callbacks: {
          label: function (context) {
            const value = context.raw as number;
            return `${context.dataset.label}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { size: 10 }, color: "#555" },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: "rgba(180, 180, 180, 0.1)" },
        ticks: {
          font: { size: 10 },
          color: "#555",
          callback: function (value) {
            const numericValue = typeof value === "string" ? parseFloat(value) : value;
            if (numericValue >= 1_000_000) return (numericValue / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
            if (numericValue >= 1_000) return (numericValue / 1_000).toFixed(0) + "K";
            return numericValue.toString();
          },
        },
      },
    },
  };

  const atStart = startIndex === 0;
  const atEnd = startIndex >= Math.max(0, allYears.length - windowSize);

  return (
    <div className="p-2 bg-white rounded-md relative">
      {!hasData || !chartData ? (
        <div className="flex items-center justify-center h-64 gap-2 text-sm text-gray-500">
          <svg
            className="w-5 h-5 animate-spin text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading chart data...
        </div>
      ) : (
        <div style={{ height: "400px", width: "100%" }}>
          <Bar data={chartData} options={options} />

          {/* Navigation buttons — same arrow style as your other charts */}
          <div className="absolute inset-x-0 -bottom-4 flex justify-between px-2">
            <button
              className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E5E7EB] hover:bg-[#D1D5DB] disabled:opacity-50"
              onClick={() => setStartIndex(Math.max(0, startIndex - 1))}
              disabled={atStart}
            >
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-[#374151]" />
            </button>
            <button
              className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E5E7EB] hover:bg-[#D1D5DB] disabled:opacity-50"
              onClick={() => setStartIndex(Math.min(startIndex + 1, Math.max(0, allYears.length - windowSize)))}
              disabled={atEnd}
            >
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-[#374151]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistogramChart;